import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatToBRL } from "@/utils/formatToMoney";

import type { IDepositDraftDetail } from "src/@types/deposit-reconciliation";

const MIN_REASON = 10;

interface UndoModalProps {
  open: boolean;
  batch: IDepositDraftDetail;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export default function UndoModal({
  open,
  batch,
  isLoading,
  onCancel,
  onConfirm,
}: UndoModalProps) {
  const [reason, setReason] = useState("");
  const valid = reason.trim().length >= MIN_REASON;

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen && !isLoading) onCancel();
      }}
    >
      <DialogContent className="max-w-md z-[60]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Desfazer Conciliação #{batch.id}
          </DialogTitle>
          <DialogDescription>
            Loja {batch.storeCode} · {formatToBRL(batch.totalAmount)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm text-slate-700">
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
            <span>
              Os depósitos voltarão a ficar pendentes e poderão ser
              reconciliados novamente. As NSUs também serão liberadas.
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2 text-xs bg-slate-50 border border-slate-200 rounded p-2">
            <div>
              <div className="text-slate-500">Loja</div>
              <div className="font-medium">{batch.storeCode}</div>
            </div>
            <div>
              <div className="text-slate-500">Total</div>
              <div className="font-medium">{formatToBRL(batch.totalAmount)}</div>
            </div>
            <div>
              <div className="text-slate-500">Itens</div>
              <div className="font-medium">
                {batch.nsus.length} NSU · {batch.deposits.length} dep
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 mb-1 block">
              Motivo da desconciliação{" "}
              <span className="text-slate-500">*</span>
            </label>
            <Textarea
              placeholder="Descreva o motivo (mínimo 10 caracteres)..."
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              className="text-sm"
              disabled={isLoading}
            />
            <span
              className={`text-xs ${valid ? "text-emerald-600" : "text-slate-500"}`}
            >
              {reason.trim().length}/{MIN_REASON} caracteres mínimos
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={() => onConfirm(reason.trim())}
            disabled={!valid || isLoading}
            className="bg-[#2B0C1A] hover:bg-[#4B303C] text-white"
          >
            {isLoading ? "Desfazendo…" : "Confirmar Desconciliação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
